import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../../firebase';
import { ref, onValue } from 'firebase/database';
import { 
    FaArrowLeft, 
    FaSort,
    FaFilePdf
} from 'react-icons/fa';

const AuditInterestedWorkDetail = () => {
    const navigate = useNavigate();
    const { id } = useParams();

    const [applicants, setApplicants] = useState([]);
    const [workDetails, setWorkDetails] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        if (!id) return;

        let engData = {}, socData = {};
        let engDone = false, socDone = false;

        const processApplicants = (work) => {
            if (engDone && socDone) {
                const allRegs = [
                    ...Object.entries(engData).map(([key, val]) => ({ ...val, _key: key, type: 'subhe' })),
                    ...Object.entries(socData).map(([key, val]) => ({ ...val, _key: key, type: 'society' }))
                ];

                const interested = allRegs.filter(reg => {
                    if (reg.work_payment && reg.work_payment[work.meetingNumber]) {
                        const selected = reg.work_payment[work.meetingNumber].selectedWorks || [];
                        return selected.some(s => s.startsWith(`${work.workNumber} -`));
                    }
                    return false;
                }).map(reg => {
                    const payment = reg.work_payment[work.meetingNumber] || {};
                    return {
                        id: reg._key,
                        name: reg.name || `${reg.firstName || ''} ${reg.middleName || ''} ${reg.surname || ''}`.trim() || reg.fullName || reg.societyName || "-",
                        phone: reg.whatsappNo || reg.mobile || reg.phoneNumber || "-",
                        type: reg.type,
                        workNumber: work.workNumber,
                        challanStatus: payment.status || "Pending",
                        challanUrl: payment.challanUrl || payment.documentUrl || null
                    };
                });

                setApplicants(interested);
                setLoading(false);
            }
        };

        const fetchApplicants = (work) => {
            const engRef = ref(db, 'ekamwatapzp/EngineerRegistrations');
            const socRef = ref(db, 'ekamwatapzp/SocietyRegistrations');

            onValue(engRef, (snap) => { engData = snap.val() || {}; engDone = true; processApplicants(work); }, { onlyOnce: true });
            onValue(socRef, (snap) => { socData = snap.val() || {}; socDone = true; processApplicants(work); }, { onlyOnce: true });
        };

        // Fetch Work Details
        const worksRef = ref(db, 'ekamwatapzp/WorkNames');
        onValue(worksRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                let foundWork = null;
                
                Object.entries(data).forEach(([key, val]) => {
                    if (foundWork) return;
                    if (val && typeof val === 'object') {
                        if (val.schemeName) {
                            if (key === id) foundWork = { id: key, meetingNumber: val.meetingNumber || "General", ...val };
                        } else {
                            Object.entries(val).forEach(([innerId, innerVal]) => {
                                if (innerId === id) foundWork = { id: innerId, meetingNumber: key, ...innerVal };
                            });
                        }
                    }
                });

                if (foundWork) {
                    setWorkDetails(foundWork);
                    fetchApplicants(foundWork);
                } else {
                    setLoading(false);
                }
            }
        }, { onlyOnce: true });

    }, [id]);

    if (loading) {
        return (
            <div className="p-20 flex flex-col items-center justify-center space-y-4">
                <div className="animate-spin text-[#085e53] text-4xl">
                    <FaSort />
                </div>
                <p className="text-gray-400 font-bold animate-pulse uppercase tracking-widest text-xs tracking-widest font-marathi">अर्जदार लोड होत आहेत...</p>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-8 bg-gray-50/50 min-h-screen font-marathi">
            {/* Header / Title Section */}
            <div className="max-w-7xl mx-auto mb-6">
                <div className="flex items-center gap-4 mb-6 no-print">
                    <button 
                        onClick={() => navigate(-1)}
                        className="p-2.5 bg-white rounded-xl shadow-sm border border-gray-100 text-gray-400 hover:text-[#085e53] transition active:scale-95"
                    >
                        <FaArrowLeft />
                    </button>
                </div>
                <h1 className="text-xl font-bold text-gray-800 tracking-tight text-center w-full my-4">इंटेरेस्टेड वर्क लिस्ट</h1>
            </div>

            {/* Applicants Table Card */}
            <div className="max-w-7xl mx-auto bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-white text-[13px] font-bold text-gray-700 border-b border-gray-200">
                                <th className="px-6 py-4 border-r border-gray-100 w-16 text-center">#</th>
                                <th className="px-6 py-4 border-r border-gray-100 min-w-[300px]">अर्जाचे नाव</th>
                                <th className="px-6 py-4 border-r border-gray-100 w-44">फोन नंबर</th>
                                <th className="px-6 py-4 border-r border-gray-100 w-32">कामाचे नाव</th>
                                <th className="px-6 py-4 border-r border-gray-100 w-32">चलन स्टेटस</th>
                                <th className="px-6 py-4">चलन</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {applicants.map((applicant, index) => (
                                <tr key={index} className="hover:bg-gray-50/50 transition-colors">
                                    <td className="px-6 py-4 border-r border-gray-50 text-center font-bold text-gray-400">{index + 1}</td>
                                    <td className="px-6 py-4 border-r border-gray-50">
                                        <div className="font-medium text-gray-800">
                                            {applicant.name} ({applicant.type === 'subhe' ? 'सिव्हिल' : 'मजूर'}) - (0)
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 border-r border-gray-50 font-medium text-gray-700">{applicant.phone}</td>
                                    <td className="px-6 py-4 border-r border-gray-50 font-medium text-gray-700">{applicant.workNumber}</td>
                                    <td className="px-6 py-4 border-r border-gray-50">
                                        <span className={`font-bold ${applicant.challanStatus === 'Completed' || applicant.challanStatus === 'Approved' ? 'text-green-600' : 'text-[#f6c23e]'}`}>
                                            {applicant.challanStatus}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        {applicant.challanUrl ? (
                                            <div className="flex justify-center">
                                                <a href={applicant.challanUrl} target="_blank" rel="noopener noreferrer" className="p-1 border border-red-200 rounded hover:bg-red-50 transition-colors">
                                                    <FaFilePdf size={18} className="text-red-500" />
                                                </a>
                                            </div>
                                        ) : (
                                            <span className="text-gray-400 text-xs font-semibold">No document found</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {applicants.length === 0 && (
                                <tr>
                                    <td colSpan="6" className="px-8 py-20 text-center text-gray-400 font-bold italic">
                                        या कामासाठी कोणतेही इंटरेस्टेड अर्ज आढळले नाहीत.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <style>{`
                .font-marathi {
                    font-family: 'Hind', sans-serif;
                }
                @import url('https://fonts.googleapis.com/css2?family=Hind:wght@400;500;600;700&display=swap');
                @media print {
                    .no-print { display: none !important; }
                    body { background: white !important; margin: 0; padding: 0; }
                }
            `}</style>
        </div>
    );
};

export default AuditInterestedWorkDetail;
